// Generic full-screen frame for the builders (KAN-763 / RD-UI-058). Presentation
// only: it owns the portal, the fixed 100dvh column, and the enter/exit beats.
// Deliberately NO keyboard / visualViewport / Safari workaround (iOS transition
// guard zone 2) and NO will-change / translateZ / contain / isolation on this
// layer (a forced GPU layer paints BLACK in real Safari when it is dropped).
// Transform is `none` at rest; it exists only while a transition is running.
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { foundation } from '../../../ui';
import { OVERLAY_MOTION } from '../../../ui/motion/overlayMotionTokens';
import { overlayLayers } from '../../../ui/overlayLayers';
import { components } from '../../../ui/theme/componentTokens';
import useReducedMotion from '../../../hooks/ai/useReducedMotion';
import { releaseParkedSpacersWithin } from '../../../utils/ui/inlineKeyboardSpacer';
import { emitOverlayChromeChange } from '../../../utils/ui/overlayChrome';
import { useSuppressAiFab } from '../../../contexts/AiFabSuppressionContext';

// ONE overlay curve for enter AND exit (RD-UI-192, src/ui/overlayMotion.js).
const EASE_ENTER = OVERLAY_MOTION.easing;
const EASE_EXIT = OVERLAY_MOTION.easing;
const BACKUP_SLACK_MS = 80;

/** Enter/exit timings (ms) per variant — exported for tests and the consumer. */
export const FULLSCREEN_MOTION = {
  touch: { enter: OVERLAY_MOTION.slideEnterMs, exit: OVERLAY_MOTION.slideExitMs },
  desktop: { enter: OVERLAY_MOTION.fadeMs, exit: OVERLAY_MOTION.fadeExitMs },
  reduced: { enter: 120, exit: 120 },
};

const frameBase = {
  position: 'fixed',
  inset: 0,
  height: '100dvh',
  maxHeight: '100dvh',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  boxSizing: 'border-box',
  background: foundation.color.neutral.bg,
  color: foundation.color.neutral.text,
  fontFamily: foundation.fontFamily.sans,
};

/** Clip-path that exactly covers a viewport rect (used to grow a card into the full screen without scaling anything). */
export function clipFromRect(rect) {
  const vw = typeof window === 'undefined' ? 0 : window.innerWidth;
  const vh = typeof window === 'undefined' ? 0 : window.innerHeight;
  const top = Math.max(0, rect.top);
  const left = Math.max(0, rect.left);
  const right = Math.max(0, vw - (rect.left + rect.width));
  const bottom = Math.max(0, vh - (rect.top + rect.height));
  return `inset(${top}px ${right}px ${bottom}px ${left}px round ${foundation.radii.xl})`;
}
const CLIP_OPEN_MS = 420;
/** The open clip ends past the frame's edges, so no sliver of the page shows at an edge (dvh vs the visible viewport). */
const CLIP_OVERSHOOT_PX = -24;

function motionFor({ layout, reduced, shown, isOpen, fromRect = null }) {
  // Opt-in (celebration): a rect to grow out of. Enter only; nothing scales, only the clip opens. Reduced motion ignores it.
  if (fromRect && !reduced && isOpen) {
    return {
      ms: CLIP_OPEN_MS,
      style: {
        clipPath: shown ? `inset(${CLIP_OVERSHOOT_PX}px round 0px)` : clipFromRect(fromRect),
        transition: `clip-path ${CLIP_OPEN_MS}ms ${EASE_ENTER}`,
      },
    };
  }
  const kind = reduced ? 'reduced' : layout === 'desktop' ? 'desktop' : 'touch';
  const ms = isOpen ? FULLSCREEN_MOTION[kind].enter : FULLSCREEN_MOTION[kind].exit;
  const ease = isOpen ? EASE_ENTER : EASE_EXIT;
  if (kind === 'reduced') {
    return { ms, style: { opacity: shown ? 1 : 0, transform: 'none', transition: `opacity ${ms}ms ${ease}` } };
  }
  if (kind === 'touch') {
    return {
      ms,
      style: { transform: shown ? 'none' : 'translateY(100%)', transition: `transform ${ms}ms ${ease}` },
    };
  }
  return {
    ms,
    style: {
      opacity: shown ? 1 : 0,
      transform: 'none',
      transition: `opacity ${ms}ms ${ease}`,
    },
  };
}

/**
 * @param {object} props
 * @param {boolean} props.isOpen - flips false to play the exit beat.
 * @param {() => void} [props.onExitComplete] - fired EXACTLY once per close,
 *   after the exit transition (transitionend, with a timer backup).
 * @param {'phone'|'tablet'|'desktop'} props.layout - latched by the caller.
 * @param {number} [props.zIndex]
 * @param {{top:number,left:number,width:number,height:number}|null} [props.enterFromRect] - opt-in: open by growing
 *   out of this viewport rect (clip-path, no transform). Absent/null = today's slide/fade. Ignored under reduced motion.
 * @param {boolean} [props.skipEnter] - opt-in: the layer mounts already shown (no enter transition, no two-frame wait). A hand-in
 *   from another layer that is exiting underneath (bulk run review -> running) uses it so nothing flashes. Exit is unchanged.
 * @param {object} [props.dialogProps] - spread on the root (role/aria/data-*).
 * @param {import('react').MutableRefObject<HTMLElement|null>} [props.frameRef] - opt-in: receives the frame element (a sender that
 *   stages a modalHandoff rect measures it).
 */
export default function FullScreenFlowShell({
  isOpen,
  onExitComplete,
  layout,
  zIndex = overlayLayers.manualInvoiceModal,
  dialogProps = {},
  enterFromRect = null,
  skipEnter = false,
  frameRef = null,
  children,
}) {
  const reduced = useReducedMotion();
  const [mounted, setMounted] = useState(Boolean(isOpen));
  // Full-screen pages hide the floating AI button too (it covered bottom-bar
  // actions such as "Registrar crédito" — KAN-786).
  useSuppressAiFab(mounted);
  const [shown, setShown] = useState(Boolean(skipEnter && isOpen));
  const exitDoneRef = useRef(true);
  const timerRef = useRef(null);
  const onExitRef = useRef(onExitComplete);
  onExitRef.current = onExitComplete;

  const clearTimer = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  };

  // The shell unmounts ITSELF once the exit has played. It used to rely on the caller
  // to unmount it from onExitComplete; a caller that stays mounted (customer edit on
  // admin) left an invisible full-page layer that swallowed every click on web until
  // a reload (2026-10-28).
  const finishExit = useCallback(() => {
    if (exitDoneRef.current) return;
    exitDoneRef.current = true;
    clearTimer();
    setMounted(false);
    if (onExitRef.current) onExitRef.current();
  }, []);

  // Enter: mount, then flip `shown` one frame later so the browser has painted
  // the off-screen state the transition starts from.
  useEffect(() => {
    if (!isOpen) return undefined;
    exitDoneRef.current = false;
    clearTimer();
    setMounted(true);
    if (skipEnter) { setShown(true); return undefined; }
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setShown(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps -- skipEnter is read at open only

  // Exit: slide/fade out, then report once (timer backs up transitionend).
  const wasOpenRef = useRef(Boolean(isOpen));
  useEffect(() => {
    if (isOpen) {
      wasOpenRef.current = true;
      return;
    }
    if (!wasOpenRef.current) return;
    wasOpenRef.current = false;
    setShown(false);
    const { ms } = motionFor({ layout, reduced, shown: false, isOpen: false });
    clearTimer();
    timerRef.current = setTimeout(finishExit, ms + BACKUP_SLACK_MS);
  }, [isOpen, layout, reduced, finishExit]);

  useEffect(() => () => clearTimer(), []);

  // Hand-in dim (see `holdDim` below): off once the growing clip has covered the viewport; re-armed for a new rect.
  const [dimGone, setDimGone] = useState(false);
  useEffect(() => {
    setDimGone(false);
    if (!enterFromRect || !isOpen) return undefined;
    const timer = setTimeout(() => setDimGone(true), CLIP_OPEN_MS + BACKUP_SLACK_MS);
    return () => clearTimeout(timer);
  }, [enterFromRect, isOpen]);

  // Pop-ups opened over this frame bound themselves by ITS header/bar: tell the
  // touch reserves when it appears and when it is gone.
  useEffect(() => {
    if (!mounted) return undefined;
    emitOverlayChromeChange();
    return emitOverlayChromeChange;
  }, [mounted]);
  // The enter slide has settled: re-measure the reserves.
  useEffect(() => {
    if (mounted && shown) emitOverlayChromeChange();
  }, [mounted, shown]);

  // KAN-930: "scroll once, stay" — the keyboard guard parks its temporary space
  // under this root; release it when the flow unmounts.
  const rootRef = useRef(null);
  const setRoot = useCallback((node) => {
    rootRef.current = node;
    if (frameRef) frameRef.current = node;
  }, [frameRef]);
  useEffect(() => {
    if (!mounted) return undefined;
    const node = rootRef.current;
    return () => releaseParkedSpacersWithin(node);
  }, [mounted]);

  if (!mounted || typeof document === 'undefined') return null;

  // Hand-off in (`enterFromRect`): the sender's dim vanishes with it, so a matching scrim sits under the growing rect from
  // frame 1 (ONE constant dim) until the clip has covered the viewport. Reduced motion has no growth: no scrim, plain fade.
  const holdDim = Boolean(enterFromRect) && !reduced && isOpen && !dimGone;
  const { style: motionStyle } = motionFor({ layout, reduced, shown, isOpen, fromRect: enterFromRect });

  return createPortal(
    <>
    {holdDim ? (
      <div aria-hidden data-testid="fullscreen-handoff-dim" style={{ position: 'fixed', inset: 0, zIndex, background: components.overlay.scrim, pointerEvents: 'none' }} />
    ) : null}
    <div
      ref={setRoot}
      role="dialog"
      aria-modal="true"
      data-layout={layout}
      {...dialogProps}
      data-cvx-kb-scroll-stays=""
      data-cvx-overlay-frame=""
      // A closing (or not-yet-shown) layer never takes a click: taps fall through to the page under it.
      style={{ ...frameBase, zIndex, ...motionStyle, ...(dialogProps.style || {}), ...(isOpen ? null : { pointerEvents: 'none' }) }}
      onTransitionEnd={(e) => {
        if (e.target !== e.currentTarget || isOpen) return;
        if (e.propertyName === 'transform' || e.propertyName === 'opacity') finishExit();
      }}
    >
      {children}
    </div>
    </>,
    document.body,
  );
}
